import { useMemo } from 'react';
import { usePlayableComponent } from '../shared/playable-component';
import Player from 'approot/video/player';
import { ViewProps } from './video.constants';
import { PlayableComponentCover } from '../shared/playable-component.style';
import { useContainerDimensions } from '../shared/component-height/component-dimensions.hooks';
import { ComponentAspectRatio } from '../shared/component-height/component-dimensions';
import { ComponentAbsoluteWrapper } from '../shared/component-height/component-dimensions.style';
import { ComponentStartCover } from '../shared/component-parts';
import {
  getClosestImageUrl,
  ImageDisplayQuality,
} from 'approot/shared/images/imgix-responsive';
import VideoInfoModal from './video-info-modal';
import { hasAttributionContent } from 'approot/shared/stimulus/logic/has-attribution-content';

const VideoView = ({
  properties,
  type,
  stimulusResourceProperties,
  onStimulusOpen,
}: ViewProps & {
  onStimulusOpen?: () => void;
}) => {
  const { isCovered, setIsCovered } = usePlayableComponent();
  const { style, setContainerElement, elementDimensions } =
    useContainerDimensions({
      aspectRatio: 16 / 9,
    });

  const backgroundImage = useMemo(
    () =>
      stimulusResourceProperties?.xlCoverImageUrls?.length
        ? getClosestImageUrl({
            imageUrls: stimulusResourceProperties.xlCoverImageUrls,
            containerSize: elementDimensions?.width
              ? Math.max(elementDimensions.width, elementDimensions.height)
              : window.innerWidth,
            quality: ImageDisplayQuality.High,
          })
        : stimulusResourceProperties?.coverImage?.original,
    [stimulusResourceProperties, elementDimensions]
  );

  return stimulusResourceProperties ? (
    <ComponentAspectRatio dimensions={properties.dimensionData}>
      <ComponentAbsoluteWrapper ref={setContainerElement}>
        {isCovered ? (
          <ComponentStartCover
            type={type}
            buttonLabel="Play"
            resourceName={stimulusResourceProperties.resourceName || ''}
            backgroundImage={backgroundImage}
            style={style}
            onClick={() => {
              setIsCovered(false);
              onStimulusOpen?.();
            }}
            data-selector="btn-play"
            extraContent={
              stimulusResourceProperties?.attributions &&
              (stimulusResourceProperties?.attributions.contentWarning ||
                hasAttributionContent(
                  stimulusResourceProperties?.attributions
                )) && (
                <VideoInfoModal
                  attribution={stimulusResourceProperties.attributions}
                />
              )
            }
          />
        ) : (
          <PlayableComponentCover
            $backgroundImg={backgroundImage}
            $fullBorderRadius
            $noPaddingTop
            style={style}
          >
            <Player
              autoPlay
              name={stimulusResourceProperties.resourceName}
              embed={stimulusResourceProperties.videoData[0].embed}
            />
          </PlayableComponentCover>
        )}
      </ComponentAbsoluteWrapper>
    </ComponentAspectRatio>
  ) : null;
};

export default VideoView;
